import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { IconLock, IconEye, IconEyeOff } from '@tabler/icons-react';
import { authApi } from 'api/authApi';
import { toast } from 'react-toastify';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card" style={{ textAlign: 'center', padding: '48px 36px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
            <h3 style={{ fontWeight: 700 }}>Invalid Link</h3>
            <p style={{ color: '#5c6b73', fontSize: '14px' }}>This password reset link is invalid or has expired.</p>
            <Link to="/forgotPassword" className="login-card__btn" style={{ display: 'inline-block', lineHeight: '48px', textDecoration: 'none', marginTop: '16px', width: 'auto', padding: '0 32px' }}>
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({ token, password });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const message = err?.response?.data?.message || 'Reset failed. The link may have expired.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card" style={{ textAlign: 'center', padding: '48px 36px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
            <h3 style={{ fontWeight: 700 }}>Password Reset!</h3>
            <p style={{ color: '#5c6b73', fontSize: '14px' }}>Redirecting to login…</p>
            <Link to="/login" className="login-card__btn" style={{ display: 'inline-block', lineHeight: '48px', textDecoration: 'none', marginTop: '16px', width: 'auto', padding: '0 32px' }}>
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-card__brand">
            <h1 className="login-card__title">New Password</h1>
            <p className="login-card__subtitle">Create a strong password</p>
          </div>

          <div className="login-card__divider" />

          <form onSubmit={handleSubmit} className="login-card__form">
            <div className="login-field">
              <label className="login-field__label" htmlFor="new-password">New Password</label>
              <div className="login-field__input-wrap">
                <IconLock size={18} className="login-field__icon" />
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  autoComplete="new-password"
                  className="login-field__input"
                />
                <button type="button" className="login-field__toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1} aria-label="Toggle password visibility">
                  {showPassword ? <IconEye size={18} /> : <IconEyeOff size={18} />}
                </button>
              </div>
            </div>

            <div className="login-field">
              <label className="login-field__label" htmlFor="confirm-password">Confirm Password</label>
              <div className="login-field__input-wrap">
                <IconLock size={18} className="login-field__icon" />
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  autoComplete="new-password"
                  className="login-field__input"
                />
                <button type="button" className="login-field__toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex={-1} aria-label="Toggle password visibility">
                  {showConfirmPassword ? <IconEye size={18} /> : <IconEyeOff size={18} />}
                </button>
              </div>
            </div>

            {error && <div style={{ color: '#ce392c', fontSize: '13px', marginBottom: '16px', padding: '10px 14px', background: 'rgba(206,57,44,0.06)', borderRadius: '10px', border: '1px solid rgba(206,57,44,0.15)' }}>{error}</div>}

            <button type="submit" className="login-card__btn" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '18px' }}>
              <Link to="/login" style={{ color: '#5c6b73', fontSize: '13px', textDecoration: 'none', fontWeight: 600 }}>
                ← Back to Login
              </Link>
            </div>
          </form>

          <p className="login-card__footer">
            &copy; {new Date().getFullYear()} SiteSync &middot; All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
